import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  CheckCircle,
  XCircle,
  ArrowClockwise,
  WebhooksLogo,
  ShieldCheck,
} from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

const ALL_WEBHOOK_EVENTS = [
  { value: 'otp.generated', label: 'OTP Generated' },
  { value: 'otp.sent', label: 'OTP Sent' },
  { value: 'otp.verified', label: 'OTP Verified' },
  { value: 'otp.expired', label: 'OTP Expired' },
  { value: 'otp.rejected', label: 'OTP Rejected' },
  { value: 'otp.failed', label: 'OTP Failed' },
];

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function SecretField({ config, newSecret, setNewSecret, showSecretInput, setShowSecretInput }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Signing Secret</Label>
        {config.webhook_secret_set && !showSecretInput && (
          <Button variant="ghost" size="sm" onClick={() => setShowSecretInput(true)} className="text-xs h-auto py-1">
            Change Secret
          </Button>
        )}
      </div>
      {(!config.webhook_secret_set || showSecretInput) ? (
        <Input
          type="password"
          value={newSecret}
          onChange={(e) => setNewSecret(e.target.value)}
          placeholder="Enter a signing secret for HMAC-SHA256 verification"
          data-testid="webhook-secret-input"
        />
      ) : (
        <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-md border text-sm text-slate-600">
          <ShieldCheck size={16} className="text-emerald-500" />
          Secret is configured
        </div>
      )}
      <p className="text-xs text-slate-500">
        Payloads are signed with HMAC-SHA256. Verify via the <code className="bg-slate-100 px-1 rounded">X-Webhook-Signature</code> header.
      </p>
    </div>
  );
}

function WebhookConfigForm({ config, setConfig, newSecret, setNewSecret, showSecretInput, setShowSecretInput, onSave, onTest, isSaving, isTesting, testResult }) {
  const toggleEvent = (eventValue) => {
    setConfig(prev => {
      const events = prev.webhook_events || [];
      if (events.includes(eventValue)) {
        return { ...prev, webhook_events: events.filter(e => e !== eventValue) };
      }
      return { ...prev, webhook_events: [...events, eventValue] };
    });
  };

  return (
    <Card className="dashboard-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2" data-testid="webhooks-config-title">
          <WebhooksLogo size={20} weight="duotone" /> Webhook Configuration
        </CardTitle>
        <CardDescription>Receive real-time notifications for OTP events via HTTP POST</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-slate-200">
          <div>
            <Label className="text-sm font-medium">Enable Webhooks</Label>
            <p className="text-xs text-slate-500">Receive HTTP POST notifications for OTP events</p>
          </div>
          <Switch
            checked={config.webhook_enabled}
            onCheckedChange={(checked) => setConfig(prev => ({ ...prev, webhook_enabled: checked }))}
            data-testid="webhook-enabled-toggle"
          />
        </div>
        <div className="space-y-2">
          <Label>Endpoint URL</Label>
          <Input
            value={config.webhook_url}
            onChange={(e) => setConfig(prev => ({ ...prev, webhook_url: e.target.value }))}
            placeholder="https://your-server.com/webhooks/mojomoney"
            data-testid="webhook-url-input"
          />
        </div>
        <SecretField
          config={config}
          newSecret={newSecret}
          setNewSecret={setNewSecret}
          showSecretInput={showSecretInput}
          setShowSecretInput={setShowSecretInput}
        />

        <div className="space-y-2">
          <Label>Event Types</Label>
          <div className="grid grid-cols-2 gap-2" data-testid="webhook-events-list">
            {ALL_WEBHOOK_EVENTS.map(evt => (
              <label key={evt.value} className="flex items-center gap-2 p-2 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer text-sm">
                <Checkbox
                  checked={(config.webhook_events || []).includes(evt.value)}
                  onCheckedChange={() => toggleEvent(evt.value)}
                  data-testid={`webhook-event-${evt.value}`}
                />
                {evt.label}
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            onClick={onSave}
            disabled={isSaving}
            className="flex-1 bg-[#0B2545] hover:bg-[#13315C]"
            data-testid="save-webhook-config-button"
          >
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </Button>
          <Button
            variant="outline"
            onClick={onTest}
            disabled={isTesting || !config.webhook_url}
            data-testid="test-webhook-button"
          >
            {isTesting ? 'Sending...' : 'Send Test'}
          </Button>
        </div>

        {testResult && (
          <div className={`p-3 rounded-lg text-sm border ${testResult.success ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'}`} data-testid="webhook-test-result">
            {testResult.success
              ? `Test webhook delivered (HTTP ${testResult.status_code})`
              : `Test failed: ${testResult.error || `HTTP ${testResult.status_code}`}`}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WebhookIntegrationGuide() {
  return (
    <Card className="dashboard-card">
      <CardHeader>
        <CardTitle className="text-lg">Webhook Integration Guide</CardTitle>
        <CardDescription>How to receive and verify webhook notifications</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div>
          <h4 className="font-semibold text-slate-700 mb-1">Payload Format</h4>
          <pre className="bg-slate-900 text-slate-100 p-3 rounded-lg text-xs overflow-x-auto" data-testid="webhook-payload-example">
{`POST https://your-server.com/webhooks
Content-Type: application/json
X-Webhook-Event: otp.verified
X-Webhook-Signature: sha256=abc123...

{
  "event": "otp.verified",
  "timestamp": "2026-04-07T...",
  "data": {
    "session_id": "uuid",
    "transaction_reference": "TXN-001",
    "phone_number": "+27...",
    "otp_type": "sms"
  }
}`}
          </pre>
        </div>
        <div>
          <h4 className="font-semibold text-slate-700 mb-1">Verify Signature</h4>
          <pre className="bg-slate-900 text-slate-100 p-3 rounded-lg text-xs overflow-x-auto">
{`import hmac, hashlib

expected = hmac.new(
  secret.encode(),
  request.body,
  hashlib.sha256
).hexdigest()
received = headers["X-Webhook-Signature"]
  .replace("sha256=", "")
assert hmac.compare_digest(expected, received)`}
          </pre>
        </div>
        <div>
          <h4 className="font-semibold text-slate-700 mb-1">Retry Policy</h4>
          <p className="text-slate-600">Failed deliveries are retried <strong>3 times</strong> with delays of 1s, 5s, and 30s. Your endpoint must return a 2xx status within 10 seconds.</p>
        </div>
      </CardContent>
    </Card>
  );
}

function DeliveryLogs({ deliveries, onRefresh }) {
  return (
    <Card className="dashboard-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Delivery Logs</CardTitle>
            <CardDescription>Recent webhook delivery attempts</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-webhook-logs">
            <ArrowClockwise size={16} className="mr-1" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {deliveries.length === 0 ? (
          <div className="text-center py-10 text-slate-400" data-testid="webhook-logs-empty">
            <WebhooksLogo size={36} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">No webhook deliveries yet</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="webhook-delivery-logs">
            {deliveries.map((d) => (
              <div
                key={d.id || `${d.event_type}-${d.created_at}-${d.attempt}`}
                className={`flex items-center gap-3 p-3 rounded-lg border text-sm ${d.success ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}
                data-testid={`webhook-delivery-${d.id || d.event_type}`}
              >
                {d.success
                  ? <CheckCircle size={18} weight="fill" className="text-emerald-500 flex-shrink-0" />
                  : <XCircle size={18} weight="fill" className="text-red-500 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] font-mono">{d.event || d.event_type}</Badge>
                    {d.status_code && (
                      <span className={`text-xs font-mono ${d.success ? 'text-emerald-600' : 'text-red-600'}`}>
                        HTTP {d.status_code}
                      </span>
                    )}
                    {d.attempt > 1 && (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-200 text-amber-700">
                        Attempt {d.attempt}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{d.webhook_url}</p>
                  {d.error && <p className="text-xs text-red-600 truncate mt-0.5">{d.error}</p>}
                </div>
                <span className="text-xs text-slate-400 whitespace-nowrap">{formatTime(d.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function WebhooksManager() {
  const [config, setConfig] = useState({
    webhook_url: '',
    webhook_secret: '',
    webhook_events: ALL_WEBHOOK_EVENTS.map(e => e.value),
    webhook_enabled: false,
  });
  const [deliveries, setDeliveries] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [newSecret, setNewSecret] = useState('');
  const [showSecretInput, setShowSecretInput] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const [configRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/api/client/webhooks`, { withCredentials: true }),
        axios.get(`${API_URL}/api/client/webhooks/deliveries`, { withCredentials: true }),
      ]);
      setConfig(configRes.data);
      setDeliveries(logsRes.data.deliveries || []);
    } catch {
      toast.error('Failed to load webhook configuration');
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        webhook_url: config.webhook_url,
        webhook_events: config.webhook_events,
        webhook_enabled: config.webhook_enabled,
      };
      if (newSecret) {
        payload.webhook_secret = newSecret;
      }
      await axios.put(`${API_URL}/api/client/webhooks`, payload, { withCredentials: true });
      toast.success('Webhook configuration saved');
      setNewSecret('');
      setShowSecretInput(false);
      loadConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save webhook configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/client/webhooks/test`, {}, { withCredentials: true });
      setTestResult(res.data);
      loadConfig();
    } catch (err) {
      setTestResult({ success: false, error: err.response?.data?.detail || 'Test failed' });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <WebhookConfigForm
          config={config}
          setConfig={setConfig}
          newSecret={newSecret}
          setNewSecret={setNewSecret}
          showSecretInput={showSecretInput}
          setShowSecretInput={setShowSecretInput}
          onSave={handleSave}
          onTest={handleTest}
          isSaving={isSaving}
          isTesting={isTesting}
          testResult={testResult}
        />
        <WebhookIntegrationGuide />
      </div>
      <DeliveryLogs deliveries={deliveries} onRefresh={loadConfig} />
    </div>
  );
}
